/**
 * Maps SAI Quick Tour routes to the same lazy imports used in `app.routes.ts`.
 * Prefetching these while the user reads a step makes Next feel instant.
 */
const TOUR_ROUTE_LOADERS: Readonly<Record<string, () => Promise<unknown>>> = {
    '/overview': () => import('app/company/dashboard/project.routes'),
    '/pages/profile': () => import('app/account/profile/profile.routes'),
    '/data-sources': () => import('app/company/data-sources/dataSources.routes'),
    '/ai-agents': () => import('app/company/ai-agents/ai-agents.routes'),
    '/tracker': () => import('app/company/tracker/new-tracker.routes'),
    '/ai-chat': () => import('app/company/ai-chat/ai-chat.routes'),
};

const _inflight = new Map<string, Promise<void>>();
const _done = new Set<string>();

export function normalizeTourRoute(route: string): string {
    const path = route.split('?')[0];
    return path.startsWith('/') ? path : `/${path}`;
}

/** True when this tour route's lazy chunk has already been fetched. */
export function isTourRoutePreloaded(route: string): boolean {
    return _done.has(normalizeTourRoute(route));
}

/**
 * Prefetch the lazy chunk for a tour route (no-op if unknown / already loaded).
 * Safe to call repeatedly; concurrent callers share one promise.
 */
export function preloadTourRoute(route: string): Promise<void> {
    const key = normalizeTourRoute(route);
    if (_done.has(key)) {
        return Promise.resolve();
    }
    const existing = _inflight.get(key);
    if (existing) {
        return existing;
    }
    const loader = TOUR_ROUTE_LOADERS[key];
    if (!loader) {
        return Promise.resolve();
    }
    const promise = loader()
        .then(() => {
            _done.add(key);
            _inflight.delete(key);
        })
        .catch(() => {
            _inflight.delete(key);
        });
    _inflight.set(key, promise);
    return promise;
}
